"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { normalizeImageUrl } from "@lib/util/image-url"

const GAP_PX = 24

type RailItem = {
  id: string
  handle: string
  title: string
  thumbnail?: string | null
  images?: { url?: string | null }[]
  price?: string | null
}

type VirtualizedProductRailProps = {
  items: RailItem[]
}

const getItemWidth = () => {
  if (typeof window === "undefined") return 320
  return window.innerWidth >= 768 ? 384 : 320
}

const VirtualizedProductRail = ({ items }: VirtualizedProductRailProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const [itemWidth, setItemWidth] = useState(320)
  const [scrollState, setScrollState] = useState({
    scrollLeft: 0,
    containerWidth: 0,
  })

  useEffect(() => {
    const updateMeasurements = () => {
      const container = containerRef.current
      setItemWidth(getItemWidth())
      if (!container) return
      setScrollState({
        scrollLeft: container.scrollLeft,
        containerWidth: container.clientWidth,
      })
    }

    updateMeasurements()
    window.addEventListener("resize", updateMeasurements)

    return () => {
      window.removeEventListener("resize", updateMeasurements)
    }
  }, [])

  const handleScroll = () => {
    if (rafRef.current !== null) return
    rafRef.current = window.requestAnimationFrame(() => {
      const container = containerRef.current
      if (container) {
        setScrollState({
          scrollLeft: container.scrollLeft,
          containerWidth: container.clientWidth,
        })
      }
      rafRef.current = null
    })
  }

  const { startIndex, endIndex, leftSpacer, rightSpacer } = useMemo(() => {
    const total = items.length
    if (!total) {
      return { startIndex: 0, endIndex: 0, leftSpacer: 0, rightSpacer: 0 }
    }

    const itemFull = itemWidth + GAP_PX
    const buffer = 2
    const start = Math.max(0, Math.floor(scrollState.scrollLeft / itemFull) - buffer)
    const end = Math.min(
      total,
      Math.ceil((scrollState.scrollLeft + scrollState.containerWidth) / itemFull) + buffer
    )

    const left = start * itemFull
    const remaining = total - end
    const right = remaining > 0 ? remaining * itemFull - GAP_PX : 0

    return {
      startIndex: start,
      endIndex: end,
      leftSpacer: Math.max(0, left),
      rightSpacer: Math.max(0, right),
    }
  }, [items.length, itemWidth, scrollState.containerWidth, scrollState.scrollLeft])

  return (
    <div
      ref={containerRef}
      className="overflow-x-auto no-scrollbar pb-4"
      onScroll={handleScroll}
    >
      <div className="flex min-w-max items-stretch">
        {leftSpacer > 0 && (
          <div
            className="flex-shrink-0"
            style={{ width: leftSpacer }}
            aria-hidden="true"
          />
        )}
        {items.slice(startIndex, endIndex).map((item, sliceIndex) => {
          const globalIndex = startIndex + sliceIndex
          const image = normalizeImageUrl(item.thumbnail || item.images?.[0]?.url || null)
          const marginRight = globalIndex < items.length - 1 ? GAP_PX : 0

          return (
            <div
              key={item.id}
              className="w-80 md:w-96 flex-shrink-0"
              style={{ marginRight }}
            >
              <LocalizedClientLink
                href={`/products/${item.handle}`}
                className="group block bg-cream border border-beige rounded-lg overflow-hidden hover-lift"
              >
                <div className="relative bg-beige-light overflow-hidden aspect-square">
                  {image ? (
                    <Image
                      src={image}
                      alt={item.title}
                      fill
                      sizes="(max-width: 768px) 320px, 384px"
                      loading="lazy"
                      className="object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-brown-light">
                      No image
                    </div>
                  )}
                </div>

                <div className="p-4 flex flex-col gap-3">
                  <p className="text-lg font-serif font-bold text-foreground mb-2 line-clamp-1">
                    {item.title}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-x-2" style={{ color: "#a8372a" }}>
                      {item.price && <span className="text-base font-semibold">{item.price}</span>}
                    </div>

                    <span
                      className="px-4 py-2 text-cream rounded-full text-xs font-medium hover:brightness-[1.1] transition-all"
                      style={{ backgroundColor: "#a8372a" }}
                    >
                      View details
                    </span>
                  </div>
                </div>
              </LocalizedClientLink>
            </div>
          )
        })}
        {rightSpacer > 0 && (
          <div
            className="flex-shrink-0"
            style={{ width: rightSpacer }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  )
}

export default VirtualizedProductRail
